/* -----------------Globals--------------- */
import { useCallback, useEffect, useRef, useState } from "react";
import { InteractionManager } from "react-native";

/* -----------------Helpers & Hooks--------------- */
import { useGoToReviewSession } from "../hooks/useGoToReviewSession";
import { useReport } from "../hooks/useReport";
import { useReportAnalytics } from "../hooks/useReportAnalytics";

/* -----------------Types--------------- */
import { router, useLocalSearchParams } from "expo-router";

import { events } from "@constants/tracking";
import { useSessionConfigDialog } from "@contexts/DialogContext/dialogs/SessionConfigDialog";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import StudySessionProgressBar from "@features/session/components/StudySessionProgressBar";
import { flattenSessionItems } from "@features/session/helpers/session";
import { useSession } from "@features/session/hooks/useSession";
import { useSessionItems } from "@features/session/hooks/useSessionItems";
import useSessionStore from "@features/session/hooks/useSessionStore";
import { handleNavigateBack } from "@helpers/common";
import { useAppStore } from "@hooks/useAppStore";
import { useAppTemplate } from "@hooks/useAppTemplate";
import useExams from "@hooks/useExams";
import { isWeb } from "@memorang/helpers/platform";
import { isReportSummativeTest } from "@memorang/helpers/report";
import type { SessionItemTypes } from "@memorang/types";
import Box from "@memorang/ui/components/Box";
import Container from "@memorang/ui/components/Container";
import { CustomAppBar } from "@memorang/ui/components/CustomAppbar";
/* -----------------Child components--------------- */
import EmptyState from "@memorang/ui/components/EmptyView";
import ReportSkeleton from "@memorang/ui/components/Skeletons/ReportSkeleton";
import StickyButtonContainer from "@memorang/ui/components/StickyButtonContainer";
import { useDevice } from "@memorang/ui/useDevice";
import { useNetworkState } from "expo-network";
import isEmpty from "lodash-es/isEmpty";
import { useTranslation } from "react-i18next";
import { Appbar } from "react-native-paper";
import NoInternetSvg from "../../../../assets/no_internet.svg";
import { LanguageLearningReportScreen } from "../components/LanguageLearningReportScreen";
import { ReportView } from "../components/ReportView";
import type {
	CheckboxStateMap,
	CheckboxType,
} from "../components/dialogs/ReviewAnswersDialog";
import { getFilteredSessionItems } from "../helpers/review";

const getIsReportReady = ({
	hasReportDetails,
	itemReviewCardEnabled,
	loadingSessionItems,
	isExamContextLoading,
	examsFetched,
}: {
	hasReportDetails: boolean;
	itemReviewCardEnabled: boolean;
	loadingSessionItems: boolean;
	isExamContextLoading: boolean;
	examsFetched: boolean;
}) => {
	const sessionItemsReady = !itemReviewCardEnabled || !loadingSessionItems;
	return (
		hasReportDetails &&
		sessionItemsReady &&
		!isExamContextLoading &&
		examsFetched
	);
};

const ReportContainer = () => {
	const [showReviewDialog, setShowReviewDialog] = useState(false);

	const { isMobile } = useDevice();
	const { t } = useTranslation();
	const { isConnected } = useNetworkState();

	const appConfigCards = useAppStore((state) => state.reportView?.cards);
	const numScoreRanges = useAppStore(
		(state) => state.app.scoreRanges?.length ?? 0,
	);
	const showPointsBasedProgress = useAppStore(
		(state) => state.reportView?.formative?.showPointsBasedScore,
	);
	const { isLanguageLearningApp } = useAppTemplate();

	const shouldRenderItemReviewCard = appConfigCards?.find(
		(card) => card.type === "item-review",
	);

	const { showSessionConfig } = useSessionConfigDialog();

	const [checkboxStateMap, setCheckboxStateMap] = useState<CheckboxStateMap>({
		correct: false,
		incorrect: false,
		skipped: false,
		almostcorrect: false,
		marked: false,
	});
	const practiceTestLabel = useAppStore(
		(state) => state.exam.practiceTest?.label,
	);

	const handleChangeCheckboxStateMap = useCallback(
		(key: keyof CheckboxStateMap) => {
			setCheckboxStateMap((prev) => {
				return {
					...prev,
					[key]: !prev[key],
				};
			});
		},
		[],
	);
	const handleUpdateIncorrectCheckboxStateMap = useCallback(() => {
		setCheckboxStateMap((prev) => {
			return {
				...prev,
				incorrect: true,
			};
		});
	}, []);

	const { id: sessionNumericId } = useLocalSearchParams<{
		id: string;
	}>();

	const [fetchingReviewItems, setFetchingReviewItems] = useState(false);
	const resetSessionStore = useSessionStore((state) => state.resetSessionStore);
	const updateSession = useSessionStore((state) => state.updateSession);
	const updateCurrentBlockIndex = useSessionStore(
		(state) => state.updateCurrentBlockIndex,
	);

	const { resumeSession } = useSession();

	const {
		currentExamContext: { studyPackCollectionId },
		isExamContextLoading,
	} = useExamContext();
	const { isFetched: examsFetched } = useExams();
	const isSummativeTestFromSessionStore = useSessionStore(
		(state) => state.isSummativeTest,
	);

	const { reportDetails } = useReport();
	const { trackReportEvent } = useReportAnalytics({ reportDetails });
	const isSummativeTest = reportDetails
		? isReportSummativeTest(reportDetails, { studyPackCollectionId })
		: isSummativeTestFromSessionStore;
	const [selectedItemTypeForReview, setSelectedItemTypeForReview] = useState<
		Extract<SessionItemTypes, "QUESTION"> | undefined
	>();

	const { data: sessionItems, isPending: loadingSessionItems } =
		useSessionItems({
			sessionId: sessionNumericId,
			isSummativeTest: Boolean(isSummativeTest),
			enabled:
				!!sessionNumericId &&
				!!shouldRenderItemReviewCard &&
				!isEmpty(reportDetails),
		});

	const { goToReviewSession, isReviewLoading } = useGoToReviewSession({
		sessionId: sessionNumericId,
		isSummativeTest: Boolean(isSummativeTest),
		trackReportEvent,
	});

	const trackedReportId = useRef<string | undefined>(undefined);
	useEffect(() => {
		if (!reportDetails || trackedReportId.current === reportDetails.id) {
			return;
		}
		trackedReportId.current = reportDetails.id;
		trackReportEvent(events.reportViewed);
	}, [reportDetails, trackReportEvent]);

	const handleReview = async (checkboxStateMap: CheckboxStateMap) => {
		if (!sessionNumericId) {
			return;
		}
		setFetchingReviewItems(true);
		try {
			const selectedKeys = Object.keys(checkboxStateMap).filter(
				(key) => checkboxStateMap[key as CheckboxType],
			) as CheckboxType[];
			const response = await resumeSession({
				sessionId: Number(sessionNumericId),
			});

			const flattenedSessionItems = flattenSessionItems(
				response.sessionItems,
				isSummativeTest,
			);

			const filteredSessionItems = getFilteredSessionItems(
				flattenedSessionItems.flat(),
				selectedKeys,
				isSummativeTest,
			);

			updateCurrentBlockIndex(0);
			updateSession({
				sessionResponse: {
					...response,
					sessionItems: [filteredSessionItems],
				},
				isSummativeTest,
				inReviewMode: true,
			});

			if (filteredSessionItems.length > 0) {
				trackReportEvent(events.reviewAnswersClicked, {
					type: "QUESTION",
					sessionId: Number(sessionNumericId),
					isSummativeTest: isSummativeTest ? 1 : 0,
					numItems: filteredSessionItems.length,
					source: "review_answers_dialog",
					isAllSelected: selectedKeys.length === 0 ? 1 : 0,
					...(selectedKeys.length > 0
						? { filters: selectedKeys.join(", ") }
						: {}),
				});
				router.push({
					pathname: "/(protected)/session/[id]/review",
					params: {
						id: sessionNumericId,
						index: "1",
					},
				});
			}
			setShowReviewDialog(false);
		} finally {
			setFetchingReviewItems(false);
		}
	};

	const handleContinue = () => {
		const url = isWeb ? "/home" : "/home?isRoutedFromSession=true";
		router.replace(url);
		InteractionManager.runAfterInteractions(() => {
			resetSessionStore();
		});
	};

	const handleClickActionChip = (type: string) => {
		// trackCustomEvent({
		//   eventName: events.suggestedChipClicked,
		//   type,
		// });
		switch (type) {
			case "review": {
				setSelectedItemTypeForReview("QUESTION");
				setShowReviewDialog(true);
				break;
			}
			case "study": {
				resetSessionStore();
				showSessionConfig({});
				break;
			}
			default:
				break;
		}
	};

	const handleReviewButtonClick = () => {
		trackReportEvent(events.reviewAnswersDialogOpened);
		setSelectedItemTypeForReview("QUESTION");
		setShowReviewDialog(true);
	};

	const handleCloseReviewDialog = () => {
		setShowReviewDialog(false);
		setCheckboxStateMap({
			correct: false,
			incorrect: false,
			skipped: false,
			almostcorrect: false,
			marked: false,
		});
	};

	const isReportReady = getIsReportReady({
		hasReportDetails: !isEmpty(reportDetails),
		itemReviewCardEnabled: Boolean(shouldRenderItemReviewCard),
		loadingSessionItems,
		isExamContextLoading,
		examsFetched,
	});

	const breadcrumbs = isSummativeTest
		? [
				{
					label: practiceTestLabel ?? "Practice Tests",
					action: () => router.push("/practice-tests"),
				},
				{
					label: reportDetails?.examName!,
				},
				...(reportDetails
					? [
							{
								label: `Test #${reportDetails.numericId}`,
							},
						]
					: []),
			]
		: [];

	if (isConnected === false) {
		return (
			<EmptyState
				image={NoInternetSvg}
				title="You’re offline"
				subtitle="Please check your internet connection to generate your report."
			/>
		);
	}

	if (isLanguageLearningApp) {
		const isLoading = !reportDetails || Object.keys(reportDetails).length === 0;

		return (
			<LanguageLearningReportScreen
				reportDetails={reportDetails}
				isLoading={isLoading}
				handleContinue={handleContinue}
				trackReportEvent={trackReportEvent}
				isSummativeTest={isSummativeTest ?? false}
				isDiagnostic={reportDetails?.examName.includes("Diagnostic") ?? false}
				sessionItems={sessionItems}
				sessionItemsLoading={loadingSessionItems}
				shouldRenderItemReviewCard={!!shouldRenderItemReviewCard}
				onReview={goToReviewSession}
				isReviewLoading={isReviewLoading}
			/>
		);
	}

	return (
		<>
			{isSummativeTest && (
				<CustomAppBar
					title={reportDetails?.examName}
					breadcrumbs={breadcrumbs}
					hideCommandBar
					onPressBack={() => handleNavigateBack("/(protected)/(tabs)/home")}
				/>
			)}
			<StickyButtonContainer
				buttonText={t("common.continue")}
				maxWidth="sm"
				disabled={!reportDetails}
				onPress={handleContinue}
				showButton={isMobile}
			>
				{/* biome-ignore lint/a11y/useSemanticElements: <explanation> */}
				<Box gap={16} flex={1} role="main">
					{!isSummativeTest && (
						<Appbar.Header
							style={{
								backgroundColor: "transparent",
								height: 80,
							}}
						>
							<Container>
								<StudySessionProgressBar
									sessionType={reportDetails?.reportSessionType}
								/>
							</Container>
						</Appbar.Header>
					)}
					{reportDetails && isReportReady ? (
						<ReportView
							reportDetails={reportDetails}
							fetchingReviewItems={fetchingReviewItems}
							handleContinue={handleContinue}
							handleClickActionChip={handleClickActionChip}
							checkboxStateMap={checkboxStateMap}
							isSummativeTest={isSummativeTest}
							handleTracking={trackReportEvent}
							handleChangeCheckboxStateMap={handleChangeCheckboxStateMap}
							handleUpdateIncorrectCheckboxStateMap={
								handleUpdateIncorrectCheckboxStateMap
							}
							sessionItems={sessionItems}
							loadingSessionItems={loadingSessionItems}
							handleReviewButtonClick={handleReviewButtonClick}
							handleCloseReviewDialog={handleCloseReviewDialog}
							selectedItemTypeForReview={selectedItemTypeForReview}
							handleReview={handleReview}
							showReviewDialog={showReviewDialog}
							shouldRenderItemReviewCard={!!shouldRenderItemReviewCard}
						/>
					) : (
						<ReportSkeleton
							isSummativeTest={isSummativeTest}
							showScoreRanges={!showPointsBasedProgress}
							numScoreRanges={numScoreRanges}
						/>
					)}
				</Box>
			</StickyButtonContainer>
		</>
	);
};

export default ReportContainer;
